import { pickBy, groupBy } from "lodash-es";
import type { SetRequired } from "type-fest";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import Condition from "@editor/items/condition/Condition";

import type { ConditionItem } from "./ConditionItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";
import { ensureConditionValidity } from "./conditionFunctions";

export const conditionItemStructure: ConditionItem = {
  type: "condition",
  id: 0,
  uid: "condition_0",

  srcType: "requirement",
  srcId: null,

  activatorType: null, // target (value), resource (value), action (occurances), round (number), roundRange (roundStart)(roundEnd), always
  activatorId: null,
  activatorProperty: null,
  activatorPropertyVariant: null, // only needed for valueChange. positive | negative

  operator: null,

  activatorStart: null,
  activatorStartType: null,
  activatorEnd: null,
  activatorEndType: null,

  comparedType: "value",
  comparedValue: 0,
  comparedValueType: "number",
  comparedId: null,
  comparedProperty: null,

  comparedStartType: null,
  comparedStart: null,
  comparedEndType: null,
  comparedEnd: null,

  doc: "",
  order: -1,
  createdAt: -1
};

export const useConditionStore = defineItemStore(
  "model/conditions",
  conditionItemStructure,
  {
    getters: {
      /**
       * Check if a condition applies in a given round
       */
      evaluate() {
        return (id: number, rounds: SimulationRoundItem[]) => {
          if (rounds.length === 0) return false;
          const condition = new Condition(this.items[id]);
          return condition.evaluate(rounds);
        };
      },
      firstFalse() {
        return (ids: number[], rounds: SimulationRoundItem[]) => {
          if (rounds.length === 0) return undefined;
          for (const id of ids) {
            const condition = new Condition(this.items[id]);
            if (!condition.complete) continue;
            if (!condition.evaluate(rounds)) {
              return condition;
            }
          }
        };
      },

      getByActivator(state) {
        return (activator: { type: string; id: number }) =>
          pickBy(
            state.items,
            (condition) =>
              condition.activatorType === activator.type &&
              condition.activatorId === activator.id
          );
      },
      getBySource(): (source: { type: string; id: number }) => ConditionItem[] {
        return (source: { type: string; id: number }) => {
          const allOfType = this.bySrcType[source.type] || [];
          return allOfType.filter((condition) => condition.srcId === source.id);
        };
      },
      getIdsBySource(): (source: { type: string; id: number }) => number[] {
        return (source: { type: string; id: number }) => {
          if (source == null) {
            console.warn("source is null");
            return [];
          }
          return this.getBySource(source).map((condition) => condition.id);
        };
      },
      bySrcType(): Record<string, ConditionItem[]> {
        return groupBy(this.orderedItems, "srcType");
      },
      byTrigger(): Record<number, ConditionItem[]> {
        return groupBy(this.bySrcType.incidentTrigger, "srcId");
      },
      byRequirement(): Record<number, ConditionItem[]> {
        return groupBy(this.bySrcType.requirement, "srcId");
      }
    },
    actions: {
      /**
       * Create a new condition that is valid.
       */
      async createValid(
        newConditionData: SetRequired<Partial<ConditionItem>, "activatorType">
      ) {
        const newCondition = await this.create(newConditionData);

        const changes = {};
        ensureConditionValidity(newCondition, changes);
        if (Object.keys(changes).length > 0) {
          this.update({
            ...changes,
            id: newCondition.id
          });
        }

        return newCondition;
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHotItemStore(useConditionStore, import.meta.hot)
  );
}
